// Shared intake queue list + status tabs for OI-3 review surfaces.

import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { SectionCard } from "@/components/common/SectionCard";
import type { OpsOrderIntakeRow } from "@/lib/orderIntakeClient";
import { SOURCE_LABELS } from "@/components/manufacturing/ManufacturingOrderIntakeShared";
import {
  handoffAgeLabel,
  intakeBuildingLabel,
  intakeHandoffTitle,
  salePathLabel,
} from "@/lib/ops/handoffIdentity";

import { buildIntakePaperworkChecks } from "./opsIntakePaperworkCheck";
import { INTAKE_TABS, type IntakeTab } from "./useOrderIntakeReview";

export function tabLabelWithCount(label: string, isActive: boolean, total: number | null): string {
  if (!isActive || total == null) return label;
  return `${label} (${total})`;
}

export function emptyStateMessage(tab: IntakeTab): string {
  switch (tab) {
    case "pending_review":
      return "No orders awaiting review.";
    case "on_hold":
      return "No held orders.";
    case "accepted":
      return "No accepted orders in this list.";
    case "cancelled":
      return "No cancelled orders.";
  }
}

export function OrderIntakeTabBar({
  activeTab,
  onChange,
  total,
}: {
  activeTab: IntakeTab;
  onChange: (tab: IntakeTab) => void;
  total: number | null;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {INTAKE_TABS.map((tab) => {
        const isActive = tab.id === activeTab;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onChange(tab.id)}
            className={
              "rounded-full px-3 py-1.5 text-sm font-medium transition-colors " +
              (isActive
                ? "bg-indigo-600 text-white"
                : "bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700")
            }
          >
            {tabLabelWithCount(tab.label, isActive, total)}
          </button>
        );
      })}
    </div>
  );
}

export function OrderIntakeRowList({
  rows,
  loading,
  activeTab,
  selectedId,
  onSelect,
  title = "Queue",
}: {
  rows: OpsOrderIntakeRow[];
  loading: boolean;
  activeTab: IntakeTab;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  title?: string;
}) {
  const queueTarget = { [FIREFLY_TARGET_ATTR]: "intake-review-queue" };

  if (loading) {
    return (
      <div {...queueTarget}>
        <SectionCard title={title}>
          <div className="text-sm text-gray-500 dark:text-zinc-400">Loading intake rows…</div>
        </SectionCard>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div {...queueTarget}>
        <SectionCard title={title}>
          <div className="text-sm text-gray-500 dark:text-zinc-400">{emptyStateMessage(activeTab)}</div>
        </SectionCard>
      </div>
    );
  }

  return (
    <div {...queueTarget}>
      <SectionCard title={title}>
      <ul className="divide-y divide-gray-100 dark:divide-zinc-700">
        {rows.map((row) => {
          const isActive = row.id === selectedId;
          const title = intakeHandoffTitle(row);
          const missing = buildIntakePaperworkChecks(row).filter((check) => check.status === "fail").length;
          const subtitle = [
            intakeBuildingLabel(row),
            salePathLabel(row.proposed_sale_path),
            handoffAgeLabel(row.created_at),
            missing ? `${missing} missing` : null,
          ]
            .filter(Boolean)
            .join(" · ");
          const secondary = [SOURCE_LABELS[row.source] ?? row.source, row.external_source_id]
            .filter(Boolean)
            .join(" · ");
          return (
            <li key={row.id}>
              <button
                type="button"
                onClick={() => onSelect(isActive ? null : row.id)}
                className={
                  "w-full rounded-md px-3 py-3 text-left transition-colors " +
                  (isActive
                    ? "bg-indigo-50 text-indigo-900 dark:bg-indigo-950/40 dark:text-indigo-100"
                    : "text-gray-800 hover:bg-gray-50 dark:text-zinc-200 dark:hover:bg-zinc-800/60")
                }
              >
                <div className="text-sm font-semibold">{title}</div>
                {subtitle ? (
                  <div className="mt-0.5 text-xs text-gray-700 dark:text-zinc-300">{subtitle}</div>
                ) : null}
                <div className="mt-0.5 text-xs text-gray-500 dark:text-zinc-400">{secondary}</div>
                {row.hold_reason ? (
                  <div className="mt-1 text-xs italic text-amber-700 dark:text-amber-300">
                    Hold: {row.hold_reason}
                  </div>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
      </SectionCard>
    </div>
  );
}
